/**
 * @file CompanyBillingAddressFields.tsx
 * @brief Compact billing address form for corporate card setup.
 *
 * @details
 * Street uses Geoapify autocomplete (Search sheet) shared with company
 * create/edit dialogs. Country stays a name string for CountryPicker.
 * `flush` drops card chrome when nested in a FormSheet section.
 *
 * @author DES
 * @date 19-06-2026
 * @updated 29-07-2026 — flush variant for FormSheet
 */

import type { ReactNode } from "react";
import { Building, Globe, Hash, Map as MapIcon, MapPin } from "lucide-react";
import { Input } from "@/components/ui/Input";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { CountryPicker } from "@/components/ui/CountryPicker";
import { AddressAutocomplete } from "@/components/ui/AddressAutocomplete";
import { cn } from "@/shared/theme/cn";

export type BillingAddressFormValues = {
  address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
};

interface CompanyBillingAddressFieldsProps {
  values: BillingAddressFormValues;
  onChange: (values: BillingAddressFormValues) => void;
  disabled?: boolean;
  /** No border/card — parent FormSheetSection owns the title. */
  flush?: boolean;
  /** Leading brand icons on each bare field (Company profile, 01-10-2026). */
  icons?: boolean;
}

export function CompanyBillingAddressFields({
  values,
  onChange,
  disabled,
  flush = false,
  icons = false,
}: CompanyBillingAddressFieldsProps) {
  const ico = (node: ReactNode) => (icons ? node : undefined);
  const set = (patch: Partial<BillingAddressFormValues>) =>
    onChange({ ...values, ...patch });

  return (
    <div
      className={cn(
        "space-y-3",
        !flush && "rounded-lg border bg-muted/20 p-4",
      )}
    >
      {flush ? null : (
        <div>
          <p className="text-sm font-medium">Billing address</p>
          <p className="text-xs text-muted-foreground">
            Required for some corporate cards and invoices. Saved on your
            company profile.
          </p>
        </div>
      )}

      <AddressAutocomplete
        id="billing-address"
        label="Street address"
        value={values.address}
        onChange={(street) => set({ address: street })}
        onSelect={(parts) =>
          onChange({
            address: parts.address,
            city: parts.city || values.city,
            state: parts.state || values.state,
            postal_code: parts.postal_code || values.postal_code,
            country: parts.country || values.country,
          })
        }
        disabled={disabled}
        placeholder="221B Baker Street"
        icon={ico(<MapPin />)}
      />

      <div className="grid gap-3 sm:grid-cols-2">
        <FormFieldGroup label="City" htmlFor="billing-city" icon={ico(<Building />)}>
          <Input
            capitalizeFirst
            id="billing-city"
            value={values.city}
            onChange={(e) => set({ city: e.target.value })}
            disabled={disabled}
          />
        </FormFieldGroup>
        <FormFieldGroup label="State / region" htmlFor="billing-state" icon={ico(<MapIcon />)}>
          <Input
            capitalizeFirst
            id="billing-state"
            value={values.state}
            onChange={(e) => set({ state: e.target.value })}
            disabled={disabled}
          />
        </FormFieldGroup>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <FormFieldGroup label="Postal code" htmlFor="billing-postal" icon={ico(<Hash />)}>
          <Input
            id="billing-postal"
            autoCapitalize="characters"
            value={values.postal_code}
            onChange={(e) => set({ postal_code: e.target.value })}
            disabled={disabled}
          />
        </FormFieldGroup>
        <FormFieldGroup label="Country" icon={ico(<Globe />)}>
          <CountryPicker
            value={values.country || null}
            onChange={(name) => set({ country: name ?? "" })}
            disabled={disabled}
            sheetSubtitle="Country on your corporate billing address."
          />
        </FormFieldGroup>
      </div>
    </div>
  );
}
